/**
 * First-login voiceprint enrollment prompt.
 *
 * Rendered at most once per user (tracked by `preferences.voiceprintPromptDismissedAt`
 * on the backend). The doctor can:
 *   - Enroll now → opens the full capture + consent flow inline
 *   - Remind me later → sets the dismissal timestamp so the modal stops showing
 *
 * Kept deliberately minimal per `feedback_clinician_ui_density.md`: one
 * decision surface, no marketing copy. When expanded, embeds the existing
 * `VoiceprintSettingsCard` so we don't duplicate the enrollment UI.
 */

import React, { useState } from 'react';
import { Mic, ShieldCheck } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from '@/hooks/useTranslation';
import { useUpdateMyPreferences } from '@/hooks/useMyProfile';
import { VoiceprintSettingsCard } from './VoiceprintSettingsCard';
import { Button } from '@/components/ui/button';

interface Props {
  open: boolean;
  /** Called after the modal is dismissed (either via "Remind me later" or after a successful enrollment). */
  onClose: () => void;
}

export const VoiceprintOnboardingModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const updatePrefs = useUpdateMyPreferences();

  const handleRemindLater = async () => {
    try {
      await updatePrefs.mutateAsync({
        voiceprintPromptDismissedAt: new Date().toISOString(),
      });
    } finally {
      onClose();
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,560px)] max-h-[85vh] overflow-auto rounded-2xl p-5 sm:p-6 shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{ backgroundColor: 'var(--color-bg-primary)' }}
        >
          <Dialog.Title asChild>
            <h2
              className="text-lg sm:text-xl font-semibold flex items-center gap-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <Mic className="w-5 h-5" />
              {t('voiceprint.onboarding.title')}
            </h2>
          </Dialog.Title>
          <Dialog.Description asChild>
            <p
              className="mt-2 text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t('voiceprint.onboarding.description')}
            </p>
          </Dialog.Description>

          <ul
            className="mt-4 space-y-2 text-sm"
            style={{ color: 'var(--color-text-primary)' }}
          >
            <li className="flex items-start gap-2">
              <ShieldCheck
                className="w-4 h-4 mt-0.5"
                style={{ color: 'var(--color-primary-600)' }}
              />
              <span>{t('voiceprint.onboarding.pointPrivacy')}</span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck
                className="w-4 h-4 mt-0.5"
                style={{ color: 'var(--color-primary-600)' }}
              />
              <span>{t('voiceprint.onboarding.pointAttribution')}</span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck
                className="w-4 h-4 mt-0.5"
                style={{ color: 'var(--color-primary-600)' }}
              />
              <span>{t('voiceprint.onboarding.pointRevoke')}</span>
            </li>
          </ul>

          {expanded ? (
            <div className="mt-5">
              <VoiceprintSettingsCard role="doctor" />
            </div>
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="secondary"
                type="button"
                onClick={handleRemindLater}
                disabled={updatePrefs.isPending}
              >
                {t('voiceprint.onboarding.remindLater')}
              </Button>
              <Button
                type="button"
                onClick={() => setExpanded(true)}
              >
                <Mic />
                {t('voiceprint.onboarding.enrollNow')}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
